class AmbassadorListController {
  /**
   * @param {HTMLElement} root Section container element
   */
  constructor(root) {
    this.root = root;
    this.sectionId = root.getAttribute('section-id');
    this.isLoading = false;
    this.hasMorePages = true;
    this.scrollObserver = null;

    this.init();
  }

  init() {
    this.filterAmbassadors();
    this.initDisciplineFilters();
    //this.initInfiniteScroll();
    this.initMutationObserver();
  }

  get grid() {
    return this.root.querySelector('[ref="grid"]');
  }

  /**
   * Filter ambassadors by discipline based on URL param
   */
  filterAmbassadors() {
    const urlParams = new URLSearchParams(window.location.search);
    const disciplineParam = urlParams.get('filter_discipline');
    const items = this.root.querySelectorAll('[data-discipline]');
    const checkboxes = this.root.querySelectorAll('.ambassador-filter__checkbox');

    /** @type {string[]} */
    let activeDisciplines = [];

    if (disciplineParam) {
      activeDisciplines = disciplineParam.split(',').map((d) => d.trim().toLowerCase());

      items.forEach((item) => {
        const itemDiscipline = (item.getAttribute('data-discipline') || '').toLowerCase();
        item.style.display = activeDisciplines.some((d) => itemDiscipline.includes(d)) ? '' : 'none';
      });
    } else {
      items.forEach((item) => {
        item.style.display = '';
      });
    }

    // Reflect active filters in checkboxes
    if (checkboxes.length) {
      checkboxes.forEach((checkbox) => {
        const value = checkbox.value.toLowerCase();
        checkbox.checked = activeDisciplines.includes(value);
      });
    }
  }

  /**
   * Get the actual page parameter name from pagination links (could be 'page' or 'page_xxx')
   */
  getPageParamName() {
    const pagination = this.root.querySelector('.pagination');
    if (pagination) {
      const anyLink = pagination.querySelector('a');
      if (anyLink instanceof HTMLAnchorElement) {
        try {
          const linkUrl = new URL(anyLink.href, window.location.origin);
          for (const [key] of linkUrl.searchParams) {
            if (key.startsWith('page')) return key;
          }
        } catch (e) {
          const href = anyLink.getAttribute('href');
          if (href && href.includes('?')) {
            const params = new URLSearchParams(href.split('?')[1]);
            for (const [key] of params) {
              if (key.startsWith('page')) return key;
            }
          }
        }
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    for (const [key] of urlParams) {
      if (key.startsWith('page')) return key;
    }

    return 'page';
  }

  /**
   * Wire up discipline filter checkboxes to update URL param and reload
   */
  initDisciplineFilters() {
    const checkboxes = this.root.querySelectorAll('.ambassador-filter__checkbox');
    if (!checkboxes.length) return;

    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        const params = new URLSearchParams(window.location.search);
        const selected = Array.from(checkboxes)
          .filter((cb) => cb.checked)
          .map((cb) => cb.value);

        if (selected.length) {
          params.set('filter_discipline', selected.join(','));
        } else {
          params.delete('filter_discipline');
        }

        const pageParamName = this.getPageParamName();
        if (pageParamName && params.has(pageParamName)) {
          params.delete(pageParamName);
        }

        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
        window.location.href = newUrl;
      });
    });
  }

  /**
   * Get next page URL from pagination
   */
  getNextPageUrl() {
    const pagination = this.root.querySelector('.pagination');
    if (pagination) {
      let nextLink = pagination.querySelector('a[rel="next"]');

      if (!(nextLink instanceof HTMLAnchorElement)) {
        const nextSpan = pagination.querySelector('.next');
        if (nextSpan) {
          const candidate = nextSpan.querySelector('a');
          if (candidate instanceof HTMLAnchorElement) nextLink = candidate;
        }
      }

      if (nextLink instanceof HTMLAnchorElement) {
        return nextLink.href;
      }
    }

    return null;
  }

  /**
   * Fetch next page and append items using section renderer
   */
  async loadNextPage() {
    if (this.isLoading || !this.hasMorePages) return;

    const nextUrl = this.getNextPageUrl();
    if (!nextUrl) {
      this.hasMorePages = false;
      return;
    }

    this.isLoading = true;

    try {
      const url = new URL(nextUrl, window.location.origin);
      if (this.sectionId) {
        url.searchParams.set('section_id', this.sectionId);
      }

      const response = await fetch(url.toString());
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const sectionIdAttr = this.sectionId ? `shopify-section-${this.sectionId}` : '';
      const sectionSelector = sectionIdAttr ? `#${sectionIdAttr}` : '.shopify-section';
      const sectionElement = doc.querySelector(sectionSelector);
      if (!sectionElement) {
        this.hasMorePages = false;
        return;
      }

      const fetchedGrid = sectionElement.querySelector('[ref="grid"]');
      if (!fetchedGrid) {
        this.hasMorePages = false;
        return;
      }

      const newItems = Array.from(fetchedGrid.querySelectorAll('[ref="cards[]"]'));
      if (newItems.length === 0) {
        this.hasMorePages = false;
        return;
      }

      const currentGrid = this.grid;
      if (currentGrid) {
        newItems.forEach((item) => {
          currentGrid.appendChild(item);
        });

        const fetchedLastPage = fetchedGrid.getAttribute('data-last-page');
        if (fetchedLastPage) {
          currentGrid.setAttribute('data-last-page', fetchedLastPage);
        }

        const fetchedPagination = sectionElement.querySelector('.pagination');
        const currentPagination = this.root.querySelector('.pagination');
        if (fetchedPagination && currentPagination) {
          currentPagination.innerHTML = fetchedPagination.innerHTML;
        } else if (fetchedPagination && !currentPagination && currentGrid.parentElement) {
          currentGrid.parentElement.insertAdjacentHTML('afterend', fetchedPagination.outerHTML);
        }

        const currentUrl = new URL(window.location.href);
        if (currentUrl.searchParams.has('section_id')) {
          currentUrl.searchParams.delete('section_id');
        }

        this.filterAmbassadors();

        setTimeout(() => {
          this.initInfiniteScroll();
        }, 100);
      }
    } catch (error) {
      console.error('Error loading next page:', error);
      this.hasMorePages = false;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Intersection Observer for infinite scroll
   */
  initInfiniteScroll() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }

    const grid = this.grid;
    if (!grid) return;

    const pagination = this.root.querySelector('.pagination');
    const targetElement = pagination || grid.lastElementChild;
    if (!targetElement) return;

    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isLoading && this.hasMorePages) {
            this.loadNextPage();
          }
        });
      },
      { rootMargin: '200px' },
    );

    this.scrollObserver.observe(targetElement);
  }

  /**
   * Observe grid changes to re-filter and re-init scroll observer
   */
  initMutationObserver() {
    const grid = this.grid;
    if (!grid) return;

    const mutationObserver = new MutationObserver(() => {
      this.filterAmbassadors();
      setTimeout(() => {
        this.initInfiniteScroll();
      }, 100);
    });

    mutationObserver.observe(grid, { childList: true, subtree: true });
  }
}

function initAmbassadorLists() {
  const init = () => {
    const sections = document.querySelectorAll('.section.product-grid-container[section-id]');
    sections.forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      if (!section.querySelector('.ambassador-grid')) return;

      new AmbassadorListController(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}

initAmbassadorLists();

export default AmbassadorListController;
